import dayjs from 'dayjs';
import {
  CustomFieldStudentAccess,
  CustomFieldType,
  CustomFieldValueSource,
  StudentProfileFieldAccess,
} from '@/types/composition-functions';
import type {
  CustomFieldFieldsFragment, CustomFieldValueFieldsFragment, StudentProfileFormFieldsFragment,
} from '@/types/operations';
import {
  buildEditModel, formatCustomFieldValue, fromInputValue, isCustomFieldTarget, sameInputValue, type CustomFieldInputValue,
} from '@/utils/customFields';

/**
 * Students fill in their own profile data: the pure logic behind the student's
 * profile form, the home-page reminder and the settings that open fields to
 * students, kept out of the components so it can be unit-tested.
 */

export type StudentProfileField = StudentProfileFormFieldsFragment['fields'][number];

/**
 * Profile fields shaped as custom field entries, so the shared custom field
 * inputs render them. A field's key stands in for its id: the form saves by
 * key ("custom:<key>" or a built-in column name).
 */
function asEntries(fields: ReadonlyArray<StudentProfileField>): CustomFieldValueFieldsFragment[] {
  return fields.map(f => ({
    value: f.value ?? null,
    filled_by: f.filled_by ?? null,
    filled_at: f.filled_at ?? null,
    field: {
      id: f.key,
      key: f.key,
      label: f.label,
      type: f.type,
      is_required: f.is_required,
      description: f.description ?? null,
      options: f.options,
      options_source: f.options_source ?? null,
      depends_on: f.depends_on ?? null,
    },
  }));
}

/** The fields the student may fill in. */
export function editableProfileEntries(fields: ReadonlyArray<StudentProfileField>): CustomFieldValueFieldsFragment[] {
  return asEntries(fields.filter(f => f.editable));
}

/**
 * The fields the school shows the student read-only, as label and display
 * text; a locked one is an answer a closed data request froze.
 */
export function readOnlyProfileRows(
  fields: ReadonlyArray<StudentProfileField>,
): Array<{ key: string; label: string; value: string; locked: boolean }> {
  return fields.filter(f => !f.editable).map(f => ({
    key: f.key,
    label: f.label,
    value: formatCustomFieldValue(f, f.value),
    locked: !!f.locked,
  }));
}

/** A tag beside each field an open data request asks for. */
export function requestedFieldTags(fields: ReadonlyArray<StudentProfileField>): Record<string, string> {
  return Object.fromEntries(fields.filter(f => f.editable && f.requested).map(f => [f.key, 'Requested']));
}

/**
 * The input model: every field's current value, the read-only ones included,
 * so an LGA the student fills in can follow a state they cannot change.
 */
export function profileFormModel(fields: ReadonlyArray<StudentProfileField>): Record<string, CustomFieldInputValue> {
  return buildEditModel(asEntries(fields));
}

/**
 * The draft: the input model and the values it was filled from (what the
 * server holds, as far as the form knows). A field counts as changed only
 * when the student made it differ from that snapshot, so a fresher copy of
 * the form, or a stored value that reads back differently (12.50 → 12.5),
 * never makes the form send a value the student did not touch.
 */
export interface ProfileDraft {
  model: Record<string, CustomFieldInputValue>;
  base: Record<string, CustomFieldInputValue>;
}

/**
 * Bring the draft up to date with the stored fields: a field the student has
 * not touched takes the stored value, one they are editing keeps their edit,
 * a new field is added and one the school closed is dropped.
 */
export function syncProfileDraft(fields: ReadonlyArray<StudentProfileField>, draft: ProfileDraft): ProfileDraft {
  const fresh = profileFormModel(fields);
  const model: Record<string, CustomFieldInputValue> = {};
  for (const field of fields) {
    // A field that became read-only shows the stored value: a leftover edit
    // would steer the LGA list and come back if the field reopens.
    const edited = field.editable && field.key in draft.base
      && !sameInputValue(field, draft.model[field.key] ?? null, draft.base[field.key] ?? null);
    model[field.key] = edited ? draft.model[field.key] ?? null : fresh[field.key] ?? null;
  }
  return { model, base: fresh };
}

/**
 * The fields the student changed, as updateMyStudentProfileForm takes them,
 * plus every required yes/no with no stored answer, sent as "No" (unticked is
 * an answer, as on the admin side).
 */
export function profileValuesPayload(
  fields: ReadonlyArray<StudentProfileField>,
  draft: ProfileDraft,
): Array<{ key: string; value: string | null }> {
  return fields
    .filter(f => f.editable && f.key in draft.model)
    .flatMap((f) => {
      const value = fromInputValue(f, draft.model[f.key] ?? null);
      if (value === null && f.type === CustomFieldType.Checkbox && f.is_required && (f.value ?? null) === null) {
        return [{ key: f.key, value: 'false' }];
      }
      return sameInputValue(f, draft.model[f.key] ?? null, draft.base[f.key] ?? null) ? [] : [{ key: f.key, value }];
    });
}

/** The longest value each built-in field accepts, so its input stops there. */
export function profileMaxLengths(fields: ReadonlyArray<StudentProfileField>): Record<string, number> {
  return Object.fromEntries(fields.filter(f => typeof f.max_length === 'number').map(f => [f.key, f.max_length as number]));
}

/** Saved values of built-in fields (student columns) by column name, to update the signed-in student. */
export function builtInValues(values: ReadonlyArray<{ key: string; value: string | null }>): Record<string, string | null> {
  return Object.fromEntries(values.filter(v => !isCustomFieldTarget(v.key)).map(v => [v.key, v.value]));
}

/** Keys of the fields the student fills in on the form, so the profile does not list them twice. */
export function editableProfileKeys(fields: ReadonlyArray<StudentProfileField>): Set<string> {
  return new Set(fields.filter(f => f.editable).map(f => f.key));
}

/** The reminder on the student's home page, or null when nothing required is missing. */
export function profileReminderText(missingRequired: number | null | undefined): string | null {
  if (!missingRequired || missingRequired < 1) return null;
  return missingRequired === 1
    ? 'Your school needs 1 detail from you.'
    : `Your school needs ${missingRequired} details from you.`;
}

/** "Filled in by the student on 22 Oct 2026", for a value the student entered; null otherwise. */
export function filledByStudentText(entry: Pick<CustomFieldValueFieldsFragment, 'value' | 'filled_by' | 'filled_at'>): string | null {
  if (entry.filled_by !== CustomFieldValueSource.Student || entry.value === null || entry.value === undefined || entry.value === '') {
    return null;
  }
  const when = entry.filled_at && dayjs(entry.filled_at).isValid() ? ` on ${dayjs(entry.filled_at).format('D MMM YYYY')}` : '';
  return `Filled in by the student${when}`;
}

// ── Settings: which fields students may fill in ─────────────────────────────

export const STUDENT_ACCESS_OPTIONS: ReadonlyArray<{ label: string; short: string; value: CustomFieldStudentAccess; hint: string }> = [
  { label: 'Staff only', short: 'Staff only', value: CustomFieldStudentAccess.Hidden, hint: 'Students never see this field.' },
  { label: 'Students can see it', short: 'Can see', value: CustomFieldStudentAccess.View, hint: 'Shown on the student’s profile, read-only.' },
  { label: 'Students can fill it in', short: 'Can fill in', value: CustomFieldStudentAccess.Edit, hint: 'Students fill it in from their profile.' },
];

/** Short label for the settings table's "Students" column. */
export function studentAccessLabel(access: CustomFieldStudentAccess | null | undefined): string {
  return STUDENT_ACCESS_OPTIONS.find(o => o.value === access)?.short ?? 'Staff only';
}

type AccessField = Pick<CustomFieldFieldsFragment, 'key' | 'label' | 'student_access' | 'depends_on' | 'is_active'>;

/**
 * Why a field set to "Students can fill it in" is still read-only for
 * students, or null when it is not: an LGA and the state it follows change
 * together, so a state locks while an LGA that follows it is not fillable,
 * and an LGA locks while its state is not shown to students.
 */
export function studentAccessNote(field: AccessField, fields: ReadonlyArray<AccessField>): string | null {
  if (!field.is_active || field.student_access !== CustomFieldStudentAccess.Edit) return null;

  const lockedFollower = fields.find(f => f.depends_on === field.key && f.is_active && f.student_access !== CustomFieldStudentAccess.Edit);
  if (lockedFollower) {
    return `Read-only for students until they can fill in "${lockedFollower.label}" too.`;
  }
  if (field.depends_on) {
    const parent = fields.find(f => f.key === field.depends_on);
    if (!parent || !parent.is_active || parent.student_access === CustomFieldStudentAccess.Hidden) {
      return `Read-only for students until "${parent?.label ?? field.depends_on}" is shown to them.`;
    }
  }
  return null;
}

export const BUILT_IN_ACCESS_OPTIONS: ReadonlyArray<{ label: string; value: StudentProfileFieldAccess }> = [
  { label: 'Off', value: StudentProfileFieldAccess.Off },
  { label: 'Optional', value: StudentProfileFieldAccess.Optional },
  { label: 'Required', value: StudentProfileFieldAccess.Required },
];

export function builtInAccessLabel(access: StudentProfileFieldAccess | null | undefined): string {
  return BUILT_IN_ACCESS_OPTIONS.find(o => o.value === access)?.label ?? 'Off';
}
